import React, { useState, useEffect } from 'react';
import PanelNotice from './PanelNotice';
import { getConfig, saveConfig } from '../services/businessConfig';

/*
 * SeatManagement (ניהול הושבה) — the business's seating/timing settings. The SEATING section
 * is now REAL: it loads from and saves to businessConfig (getBusinessConfig / updateBusinessConfig)
 * and drives the LIVE board's timing. The payments/tags sections below have no backend yet.
 */
const SeatManagement = () => {
    // Seating/timing settings (persisted via businessConfig).
    const [maxOccupancy, setMaxOccupancy] = useState(100);       // maxPercentForOrdersBeforeFull
    const [holdTime, setHoldTime] = useState(15);                // tableNotAviableWhileOrderingInMinutes
    const [reservationTime, setReservationTime] = useState(10);  // saveTableForLateCustomerInMinutes
    const [seatingDuration, setSeatingDuration] = useState(90);  // maxTimeTableIsInUseInMinutes
    const [upcomingLead, setUpcomingLead] = useState(30);        // upcomingReservationLeadMinutes (the X time)
    const [bookingCutoff, setBookingCutoff] = useState(0);       // bookingCutoffMinutesBeforeClose (0 = disabled)
    const [phoneThreshold, setPhoneThreshold] = useState(0);     // phoneBookingThresholdGuests (0 = disabled)

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState(''); // transient save feedback

    // Not-yet-backed extras (kept as local UI).
    const [requiresCreditCard, setRequiresCreditCard] = useState(false);
    const [noShowCharge, setNoShowCharge] = useState(0);
    const [tagText, setTagText] = useState('');
    const [tags, setTags] = useState([]);

    // Load the real settings on mount.
    useEffect(() => {
        let alive = true;
        (async () => {
            const c = await getConfig();
            if (!alive) return;
            setMaxOccupancy(c.maxPercentForOrdersBeforeFull);
            setHoldTime(c.tableNotAviableWhileOrderingInMinutes);
            setReservationTime(c.saveTableForLateCustomerInMinutes);
            setSeatingDuration(c.maxTimeTableIsInUseInMinutes);
            setUpcomingLead(c.upcomingReservationLeadMinutes);
            setBookingCutoff(c.bookingCutoffMinutesBeforeClose);
            setPhoneThreshold(c.phoneBookingThresholdGuests);
            setLoading(false);
        })();
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        if (!status) return undefined;
        const t = setTimeout(() => setStatus(''), 2600);
        return () => clearTimeout(t);
    }, [status]);

    const handleMaxOccupancyChange = (e) => setMaxOccupancy(Math.max(1, Math.min(100, Number(e.target.value))));
    const handleHoldTimeChange = (e) => setHoldTime(Math.max(0, Math.min(120, Number(e.target.value))));
    const handleReservationTimeChange = (e) => setReservationTime(Math.max(0, Math.min(120, Number(e.target.value))));
    const handleSeatingDurationChange = (e) => setSeatingDuration(Math.max(30, Math.min(360, Number(e.target.value))));
    const handleUpcomingLeadChange = (e) => setUpcomingLead(Math.max(0, Math.min(240, Number(e.target.value))));
    const handleBookingCutoffChange = (e) => setBookingCutoff(Math.max(0, Math.min(240, Number(e.target.value))));
    const handlePhoneThresholdChange = (e) => setPhoneThreshold(Math.max(0, Math.min(100, Number(e.target.value))));
    const handleNoShowChargeChange = (e) => setNoShowCharge(Math.max(0, Math.min(100, Number(e.target.value))));

    const handleSave = async () => {
        if (saving) return;
        setSaving(true);
        const res = await saveConfig({
            maxPercentForOrdersBeforeFull: maxOccupancy,
            tableNotAviableWhileOrderingInMinutes: holdTime,
            saveTableForLateCustomerInMinutes: reservationTime,
            maxTimeTableIsInUseInMinutes: seatingDuration,
            upcomingReservationLeadMinutes: upcomingLead,
            bookingCutoffMinutesBeforeClose: bookingCutoff,
            phoneBookingThresholdGuests: phoneThreshold,
        });
        setSaving(false);
        if (res.ok && res.config) {
            // Reflect the server's clamped values.
            setMaxOccupancy(res.config.maxPercentForOrdersBeforeFull);
            setHoldTime(res.config.tableNotAviableWhileOrderingInMinutes);
            setReservationTime(res.config.saveTableForLateCustomerInMinutes);
            setSeatingDuration(res.config.maxTimeTableIsInUseInMinutes);
            setUpcomingLead(res.config.upcomingReservationLeadMinutes);
            setBookingCutoff(res.config.bookingCutoffMinutesBeforeClose);
            setPhoneThreshold(res.config.phoneBookingThresholdGuests);
            setStatus('נשמר');
        } else {
            setStatus('שמירה נכשלה — נסו שוב');
        }
    };

    const handleAddTag = () => {
        if (tagText.trim() !== '') { setTags([...tags, tagText]); setTagText(''); }
    };
    const handleRemoveTag = (index) => setTags(tags.filter((_, i) => i !== index));

    return (
        <div>
            <h3 className='seat-title'>ניהול הושבה</h3>
            <div className='input-container max-occupancy'>
                <label>
                    אחוז תפוסה מקסימלי:
                    <input type='number' value={maxOccupancy} onChange={handleMaxOccupancyChange}
                        min='1' max='100' className='max-occupancy-input' disabled={loading || saving} />
                </label>
            </div>
            <div className='input-container hold-time'>
                <label>
                    סמן שולחן כתפוס מרגע תחילת הזמנה ל:
                    <input type='number' value={holdTime} onChange={handleHoldTimeChange}
                        min='0' max='120' className='hold-time-input' disabled={loading || saving} /> דקות
                </label>
            </div>
            <div className='input-container reservation-time'>
                <label>
                    שמירת שולחן מוזמן:
                    <input type='number' value={reservationTime} onChange={handleReservationTimeChange}
                        step='5' min='0' max='120' className='reservation-time-input' disabled={loading || saving} /> דקות
                </label>
            </div>
            <div className='input-container seating-duration'>
                <label>
                    משך ישיבה ברירת מחדל ללקוח:
                    <input type='number' value={seatingDuration} onChange={handleSeatingDurationChange}
                        step='15' min='30' max='360' className='reservation-time-input' disabled={loading || saving} /> דקות
                </label>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', marginTop: 'var(--s-1)' }}>
                    כמה זמן שולחן נחשב תפוס לאחר ישיבה — לפי זה הלוח מחשב מתי נפתח מקום פנוי לשולחן הבא.
                </div>
            </div>
            <div className='input-container upcoming-lead'>
                <label>
                    סמן שולחן כ"הזמנה קרובה" כבר:
                    <input type='number' value={upcomingLead} onChange={handleUpcomingLeadChange}
                        step='5' min='0' max='240' className='reservation-time-input' disabled={loading || saving} /> דקות לפני ההזמנה
                </label>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', marginTop: 'var(--s-1)' }}>
                    כמה זמן לפני הזמנה שנקבעה מראש הלוח מסמן את השולחן כשמור, עם מתי ההזמנה הקרובה.
                </div>
            </div>

            <div className='input-container booking-cutoff'>
                <label>
                    חסימת הזמנות אונליין
                    <input type='number' value={bookingCutoff} onChange={handleBookingCutoffChange}
                        step='5' min='0' max='240' className='reservation-time-input' disabled={loading || saving} /> דקות לפני הסגירה
                </label>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', marginTop: 'var(--s-1)' }}>
                    0 = ללא. חוסם הזמנות אונליין שהשעה שלהן קרובה מדי לסגירת היום.
                </div>
            </div>
            <div className='input-container phone-threshold'>
                <label>
                    קבוצה בגודל
                    <input type='number' value={phoneThreshold} onChange={handlePhoneThresholdChange}
                        min='0' max='100' className='reservation-time-input' disabled={loading || saving} /> ומעלה — הזמנה טלפונית בלבד
                </label>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', marginTop: 'var(--s-1)' }}>
                    0 = ללא. קבוצה בגודל זה ומעלה תופנה להזמנה טלפונית במקום אונליין.
                </div>
            </div>

            <div className='input-container' style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-3)' }}>
                <button type='button' className='pf-btn--primary' onClick={handleSave} disabled={loading || saving}
                    style={{ minHeight: 40, padding: '0 var(--s-6)', border: 'none', borderRadius: 'var(--r-md)', background: 'var(--brand)', color: 'var(--on-brand)', cursor: 'pointer' }}>
                    {saving ? 'שומר…' : 'שמירת הגדרות הושבה'}
                </button>
                {status && <span role="status" aria-live="polite" style={{ fontSize: 'var(--fs-sm)', color: status === 'נשמר' ? 'var(--success)' : 'var(--danger)' }}>{status}</span>}
            </div>

            <h3 className='seat-title'>חיובים ופקדונות</h3>
            <PanelNotice>הסעיפים הבאים (חיובים ותגיות) עדיין לא נשמרים בשרת.</PanelNotice>
            <div className='input-container credit-card-required'>
                <label>
                    <input type='checkbox' checked={requiresCreditCard}
                        onChange={(e) => setRequiresCreditCard(e.target.checked)} className='credit-card-checkbox' />
                    דרוש כרטיס אשראי לפקדון על מנת לבצע הזמנה
                </label>
            </div>
            <div className='input-container no-show-charge'>
                <label>
                    חיוב אוטומטי של
                    <input type='number' value={noShowCharge} onChange={handleNoShowChargeChange}
                        min='0' max='100' className='no-show-charge-input' />
                    % מסך ההזמנה במידה של אי הגעה ללא ביטול
                </label>
            </div>
            <h3 className='seat-title'>ניהול תגיות</h3>
            <div className='input-container tag-input'>
                <label>
                    הוסף תגית:
                    <input type='text' value={tagText} onChange={(e) => setTagText(e.target.value)} className='tag-input-field' />
                    <button type='button' className='add-time-range-button' onClick={handleAddTag}>+</button>
                </label>
            </div>
            <div className='tag-container'>
                {tags.map((tag, index) => (
                    <div key={index} className='tag'>
                        {tag}
                        <button type='button' className='remove-tag-button' onClick={() => handleRemoveTag(index)}>x</button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SeatManagement;
